import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {FACEBOOK_CHANNEL, MERCADO_ADS_CHANNEL} from '../../channels/channels';
import {flagOf} from '../../channels/mercado_ads/mercadoAds';
import {ArrowRight, ArrowsClockwise} from '../../icons';
import {Spinner} from '../../Spinner';
import {accountsPathFor, filterAccessAccounts, missingAccountKeyOf} from './platformsModel';

const NS = 'agency_v2.platforms';

// Con más cuentas que esto, la lista lleva buscador: una agencia grande trae cientos por acceso.
export const SEARCH_THRESHOLD = 8;

/**
 * Las cuentas que trae UN acceso (nombre e ID) y, debajo, «¿Falta una cuenta?» con el porqué de su
 * plataforma (cc#205). Antes la fila decía sólo «N cuentas»: quien buscaba una cuenta no sabía si
 * había llegado y reconectaba con el mismo usuario esperando otro resultado.
 *
 * El «Ver en Cuentas» conserva el destino que tenía el número (nada desaparece sin su reemplazo).
 */
export function AccessAccounts({channel, credential, reconnectable, onReconnect, reconnecting}) {
    const {t} = useTranslation();
    // `null` = cargando; un array = las cuentas; `'error'` = no se pudo leer.
    const [accounts, setAccounts] = useState(null);
    const [query, setQuery] = useState('');

    const load = useCallback(() => {
        let alive = true;
        setAccounts(null);
        new APIClient(`${API_URL}agency/channel-credentials/${encodeURIComponent(credential.id)}/ad-accounts`)
            .call('GET')
            .then((response) => {
                if (!alive) return;
                setAccounts(isOk(response) && Array.isArray(response.ad_accounts) ? response.ad_accounts : 'error');
            })
            .catch(() => alive && setAccounts('error'));
        return () => { alive = false; };
    }, [credential.id]);

    // Se relee cuando cambia el conteo: el sync de un acceso recién conectado las va trayendo.
    useEffect(load, [load, credential.ad_accounts_count]);

    const missingKey = missingAccountKeyOf(channel);
    // En Meta, si se sabe quién autorizó, el texto la nombra: «asígnale la cuenta a esa persona» no
    // dice a quién (cc#209).
    const missingText = missingKey && channel === FACEBOOK_CHANNEL && credential.platform_user_name
        ? t(`${missingKey}_named`, {name: credential.platform_user_name, interpolation: {escapeValue: false}})
        : missingKey && t(missingKey);
    const visible = Array.isArray(accounts) ? filterAccessAccounts(accounts, query) : [];

    return (
        <div className="apl-accounts" data-testid="apl-accounts">
            <p className="apl-accounts__title">{t(`${NS}.accounts.title`)}</p>

            {accounts === null && (
                <div className="apl-accounts__loader"><Spinner color="positive small"/></div>
            )}

            {accounts === 'error' && (
                <p className="apl-accounts__note">
                    {t(`${NS}.accounts.load_error`)}{' '}
                    <button type="button" className="apl-link" onClick={load}>{t(`${NS}.accounts.retry`)}</button>
                </p>
            )}

            {Array.isArray(accounts) && accounts.length === 0 && (
                <p className="apl-accounts__note">
                    {credential.is_syncing ? t(`${NS}.accounts.syncing`) : t(`${NS}.accounts.empty`)}
                </p>
            )}

            {Array.isArray(accounts) && accounts.length > 0 && (
                <>
                    {accounts.length > SEARCH_THRESHOLD && (
                        <input className="apl-accounts__search" type="search" value={query}
                               placeholder={t(`${NS}.accounts.search`)} aria-label={t(`${NS}.accounts.search`)}
                               onChange={(event) => setQuery(event.target.value)}/>
                    )}
                    {visible.length === 0 ? (
                        <p className="apl-accounts__note">
                            {t(`${NS}.accounts.no_match`, {query, interpolation: {escapeValue: false}})}
                        </p>
                    ) : (
                        <ul className="apl-accounts__list">
                            {visible.map((account) => (
                                <li key={account.id} className="apl-accounts__item">
                                    {/* Un vendedor tiene un anunciante por país: la bandera los distingue
                                        si el backend manda el país o la moneda. */}
                                    {channel === MERCADO_ADS_CHANNEL && flagOf(account) && (
                                        <span className="apl-accounts__flag" aria-hidden="true">{flagOf(account)}</span>
                                    )}
                                    <span className="apl-accounts__name">{account.name || '—'}</span>
                                    <span className="apl-accounts__id">
                                        {t(`${NS}.accounts.account_id`, {id: account.account_id})}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <Link className="apl-link apl-accounts__more" to={accountsPathFor(channel)}>
                        {t(`${NS}.accounts.see_in_accounts`)}
                        <ArrowRight size={14} aria-hidden="true"/>
                    </Link>
                </>
            )}

            {missingKey && (
                <div className="apl-missing" data-testid="apl-missing">
                    <p className="apl-missing__title">{t(`${NS}.missing.title`)}</p>
                    <p className="apl-missing__text">{missingText}</p>
                    <p className="apl-missing__text">{t(`${NS}.missing.other_user`)}</p>
                    {reconnectable && (
                        <button type="button" className="apl-btn -sm" onClick={onReconnect} disabled={reconnecting}
                                aria-busy={reconnecting || undefined}>
                            <ArrowsClockwise size={15} aria-hidden="true"/>
                            {t(`${NS}.missing.reconnect`)}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default AccessAccounts;
